import React from 'react';
import Notice from '../Notice';

// THE shared quote core. One component, both doors.
//
// A supplier must not be able to price two different ways depending on which
// entry point they came through (owner, 2026-09-19). Before this existed the
// public RFQ form and the dashboard's inline offer form collected different
// sets — the public one had price_basis / valid_days / the sample trio, the
// dashboard one had shipping cost / shipping method / origin — so the same
// deal quoted through two doors produced two incomparable rows.
//
// The core is: price, currency, MOQ, lead time, ORIGIN, note, price_basis,
// valid_days and the conditional sample trio. Nothing else belongs here.
//
// Deliberately NOT in the core:
//   · shipping_cost / shipping_method — deprecated 2026-09-19. price_basis
//     encodes who carries freight, which is what the trader compares on; a
//     free-text shipping cost beside it invites two suppliers to quote the
//     same deal two ways. The columns survive for old rows, unwritten.
//   · Est. Total — a computed DISPLAY, not an input, so it cannot create a
//     second pricing model. The dashboard renders it around this component.
//
// Presentational only: it owns no state and performs no submit. Each surface
// keeps its own submit path, because they genuinely differ — the public one
// goes through submit_public_rfq_quote (token-scoped RPC) and the dashboard
// inserts into offers directly, which RLS permits once a request is routed.

// DDP added 2026-09-21 (migration 20260921122524). Door delivery previously
// had NO representation: a supplier who trucks to the trader's address had to
// mislabel it CIF or bury it in the note, and the trader comparing two quotes
// could not see the difference.
//
// ⚠️ FOUR ENFORCEMENT POINTS, and they move together: this array, the CHECK
// constraint offers_price_basis_chk, submit_public_rfq_quote, and
// submit_factory_offer. A value here that the constraint rejects fails the
// insert; a value the constraint allows but this array omits is unpickable.
export const PRICE_BASES = ['EXW', 'FOB', 'CIF', 'DDP'];
export const VALID_DAY_OPTIONS = [15, 30, 60];
// USD only (owner, 2026-10-06). A CNY quote could be accepted and then never
// paid (current_sar_rate has no CNY rate), and the forms preselected the
// supplier's DISPLAY currency, so CNY was chosen without anyone choosing it.
// Enforced server-side by trg_offers_currency_usd. offers_currency_chk still
// allows SAR/CNY, for the rows written before this.
export const QUOTE_CURRENCIES = ['USD'];

// "≈ ¥X" under a USD price, at the admin CNY rate (admin_settings.cny_usd_rate,
// CNY per USD) — a reading aid for suppliers who price in yuan. Display only:
// nothing saved or charged comes from it. null when there is nothing to show.
// Byte-identical to the app's quoteCore.cnyHintAmount.
export function cnyHintAmount(price, cnyPerUsd) {
  const p = Number(price);
  const r = Number(cnyPerUsd);
  if (!(p > 0) || !(r > 0)) return null;
  return (p * r).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// The request asks for a sample on these two; 'none' means do not ask.
export const SAMPLE_ASKED = ['preferred', 'required'];

// Shared validation, so "same fields" also means same rules. Returns a key
// into the caller's string pack, or null when the quote is well formed.
export function validateQuoteCore({ price, leadTime, priceBasis }) {
  const priceNum = Number(price);
  if (!Number.isFinite(priceNum) || priceNum <= 0) return 'priceInvalid';
  const days = Number.parseInt(leadTime, 10);
  if (!Number.isInteger(days) || days <= 0) return 'leadTimeInvalid';
  if (!priceBasis) return 'priceBasisRequired';
  return null;
}

export default function QuoteFields({
  t,                       // the surface's string pack
  values,                  // { price, currency, moq, leadTime, origin, note,
                           //   priceBasis, validDays, sampleAvailable,
                           //   samplePrice, sampleDays }
  onChange,                // (key, value) => void
  askSample = false,       // driven by requests.sample_requirement
  sampleRequirement = '',  // 'preferred' | 'required'
  // What this supplier said they can do (profiles.delivery_terms), narrowing
  // the basis list. Defaults to everything, because an EMPTY capability means
  // "not asked yet" — narrowing on a question nobody answered would quietly
  // remove options the supplier does offer. See lib/supplierDeliveryTerms.js.
  allowedBases = PRICE_BASES,
  styles = {},             // { label, input, hint } from the host surface
  // OPTIONAL class names, for a surface that would rather reuse the global
  // form CSS (.form-label / .form-input) than restate it as inline objects.
  // Added 2026-09-21 for the edit-offer modal; purely additive. A surface that
  // passes nothing gets `className={undefined}`, which React omits entirely,
  // so the other three doors render byte-identical markup to before.
  // Inline `styles` still wins where both are given — that is the escape hatch,
  // not the default.
  classes = {},            // { label, input, hint }
  idPrefix = 'q',
  // What the trader asked for, told to the supplier with the Incoterm to
  // quote, and the warning when the chosen basis stops short of it. Both are
  // FINISHED strings from src/lib/supplierRequestTerms.js (deliveryAskText /
  // deliveryShortfallText), computed by the host, which knows the request and
  // the language. '' renders nothing. Non-blocking by design (2026-09-28):
  // a supplier may still quote a shorter basis — the trader's offer card then
  // flags it as not matching (lib/deliveryMatch.js).
  deliveryAsk = '',
  basisWarning = '',
  isAr = false,
  // CNY per USD at the admin rate, for the "≈ ¥X" reading aid under the
  // price. Omitted → no hint.
  cnyPerUsd = null,
}) {
  const label = styles.label || {};
  const input = styles.input || {};
  const hint = styles.hint || {};
  const labelCls = classes.label;
  const inputCls = classes.input;
  const hintCls = classes.hint;
  const set = (k) => (e) => onChange(k, e.target.value);
  // An offer written before USD-only keeps its own currency when edited: the
  // only option, and locked, so editing the price never rewrites it (the
  // server refuses a change to anything but USD anyway).
  const legacyCurrency = values.currency && !QUOTE_CURRENCIES.includes(values.currency)
    ? values.currency : null;
  const cnyHint = legacyCurrency ? null : cnyHintAmount(values.price, cnyPerUsd);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 2 }}>
          <label className={labelCls} style={label} htmlFor={`${idPrefix}-price`}>{t.price}</label>
          <input id={`${idPrefix}-price`} className={inputCls} style={input} inputMode="decimal"
            value={values.price} onChange={set('price')} />
        </div>
        <div style={{ flex: 1 }}>
          <label className={labelCls} style={label} htmlFor={`${idPrefix}-currency`}>{t.currency}</label>
          <select id={`${idPrefix}-currency`} className={inputCls} style={input} value={values.currency} onChange={set('currency')}
            disabled={Boolean(legacyCurrency)}>
            {(legacyCurrency ? [legacyCurrency] : QUOTE_CURRENCIES).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      {!legacyCurrency && (t.usdOnly || (cnyHint && t.cnyHint)) ? (
        <div style={{ margin: '-8px 0 14px' }}>
          {cnyHint && t.cnyHint ? <p className={hintCls} style={hint}>{t.cnyHint.replace('{amount}', cnyHint)}</p> : null}
          {t.usdOnly ? <p className={hintCls} style={hint}>{t.usdOnly}</p> : null}
        </div>
      ) : null}

      {/* price_basis and country of manufacture sit together on purpose: both
          are what the trader's customs broker asks about first. */}
      <div style={{ marginBottom: 14 }}>
        <label className={labelCls} style={label} htmlFor={`${idPrefix}-basis`}>{t.priceBasis}</label>
        <select id={`${idPrefix}-basis`} className={inputCls} style={input} value={values.priceBasis} onChange={set('priceBasis')}>
          <option value="">—</option>
          {/* A basis the supplier has ALREADY chosen always stays listed, even
              if it is outside their stated capability — otherwise opening the
              editor on an older quote silently blanks the field and the next
              save writes null over a real answer. */}
          {PRICE_BASES.filter((b) => allowedBases.includes(b) || b === values.priceBasis)
            .map((b) => <option key={b} value={b}>{t.priceBases[b]}</option>)}
        </select>
        {deliveryAsk ? <p className={hintCls} style={hint}>{deliveryAsk}</p> : null}
        <Notice tone="warn" isAr={isAr}>{basisWarning}</Notice>
      </div>

      <div style={{ marginBottom: 14 }}>
        {/* "Country of manufacture", never "Origin": the readers are Chinese
            factories reading in translation, and "origin" is ambiguous enough
            to get the shipping port typed into it (owner, 2026-09-19). */}
        <label className={labelCls} style={label} htmlFor={`${idPrefix}-origin`}>{t.origin}</label>
        <input id={`${idPrefix}-origin`} className={inputCls} style={input} placeholder={t.originPh}
          value={values.origin} onChange={set('origin')} />
        <p className={hintCls} style={hint}>{t.originHint}</p>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className={labelCls} style={label} htmlFor={`${idPrefix}-valid`}>{t.validFor}</label>
        <select id={`${idPrefix}-valid`} className={inputCls} style={input} value={values.validDays}
          onChange={(e) => onChange('validDays', Number(e.target.value))}>
          {VALID_DAY_OPTIONS.map((d) => <option key={d} value={d}>{t.validDays[d]}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className={labelCls} style={label} htmlFor={`${idPrefix}-moq`}>{t.moq}</label>
        <input id={`${idPrefix}-moq`} className={inputCls} style={input} placeholder={t.moqPh}
          value={values.moq} onChange={set('moq')} />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className={labelCls} style={label} htmlFor={`${idPrefix}-lead`}>{t.leadTime}</label>
        <input id={`${idPrefix}-lead`} className={inputCls} style={input} inputMode="numeric" placeholder={t.leadTimePh}
          value={values.leadTime} onChange={set('leadTime')} />
      </div>

      <div style={{ marginBottom: 18 }}>
        <label className={labelCls} style={label} htmlFor={`${idPrefix}-note`}>{t.note}</label>
        <textarea id={`${idPrefix}-note`} className={inputCls} style={{ ...input, minHeight: 96, resize: 'vertical' }}
          placeholder={t.notePh} value={values.note} onChange={set('note')} />
      </div>

      {/* Only when the trader asked for a sample. Every extra field costs
          completions, and most requests want no sample at all. */}
      {askSample && (
        <div style={{ marginBottom: 18, padding: 14, borderRadius: 8, background: 'var(--bg-base)', border: '1px solid var(--border-subtle)' }}>
          <span className={labelCls} style={label}>{t.sampleTitle}</span>
          <p style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)' }}>
            {t.sampleAsk[sampleRequirement]}
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text-primary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!values.sampleAvailable}
              onChange={(e) => onChange('sampleAvailable', e.target.checked)} />
            {t.sampleCanSupply}
          </label>
          {values.sampleAvailable && (
            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <label className={labelCls} style={label} htmlFor={`${idPrefix}-sample-price`}>{t.samplePrice}</label>
                <input id={`${idPrefix}-sample-price`} className={inputCls} style={input} inputMode="decimal"
                  value={values.samplePrice} onChange={set('samplePrice')} />
                <p className={hintCls} style={hint}>{t.samplePriceHint}</p>
              </div>
              <div style={{ flex: 1 }}>
                <label className={labelCls} style={label} htmlFor={`${idPrefix}-sample-days`}>{t.sampleDays}</label>
                <input id={`${idPrefix}-sample-days`} className={inputCls} style={input} inputMode="numeric"
                  value={values.sampleDays} onChange={set('sampleDays')} />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
